import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCourseList, fetchProgramById, fetchProgramId, fetchProgramImageById } from '../redux/actions/Actions';
import RequestCallback from '../RequestCallback';
import axiosinstance from '../../api/axiosinstance';
import JourneyWorkflow from './JourneyWorkflow';
import CodingBootcamp from './CodingBootcamp';
import DeveloperFullTime from './DeveloperFullTime';
import InternshipPage from './InternshipPage';
import ESPRPPage from './ESPRPPage';

/* ── Journey Workflow data per program ── */
const JOURNEY_BY_PROGRAM = {
  1: { // Coding Bootcamp
    title: 'Coding Bootcamp Journey Workflow',
    accentColor: '#7C3AED',
    steps: [
      { label: 'Enrollment & Onboarding', icon: 'enroll',      color: '#7C3AED' },
      { label: 'Foundation Training',     icon: 'book',        color: '#7C3AED' },
      { label: 'Hands-on Practice',       icon: 'training',    color: '#7C3AED' },
      { label: 'Mini Projects',           icon: 'project',     color: '#7C3AED' },
      { label: 'Mock Interviews',         icon: 'interview',   color: '#7C3AED' },
      { label: 'Capstone Project',        icon: 'capstone',    color: '#7C3AED' },
      { label: 'Resume Building',         icon: 'resume',      color: '#7C3AED' },
      { label: 'Certification',           icon: 'certificate', color: '#7C3AED' },
      { label: 'Placement Support',       icon: 'placement',   color: '#7C3AED' },
    ],
  },
  2: { // Developer Full Time
    title: 'Developer Full Time Journey Workflow',
    accentColor: '#16A34A',
    steps: [
      { label: 'Enrollment',           icon: 'enroll',      color: '#16A34A' },
      { label: 'Technical Training',   icon: 'training',    color: '#16A34A' },
      { label: 'Skill Assessments',    icon: 'assessment',  color: '#16A34A' },
      { label: 'Live Client Project',  icon: 'team',        color: '#16A34A' },
      { label: 'Agile Scrum Process',  icon: 'target',      color: '#16A34A' },
      { label: 'Code Reviews',         icon: 'review',      color: '#16A34A' },
      { label: 'Continuous Learning',  icon: 'book',        color: '#16A34A' },
      { label: 'Certification',        icon: 'certificate', color: '#16A34A' },
      { label: 'Placement Support',    icon: 'placement',   color: '#16A34A' },
    ],
  },
  3: { // Internship
    title: 'Internship Journey Workflow',
    accentColor: '#2563EB',
    steps: [
      { label: 'Student Enrollment',      icon: 'enroll',      color: '#2563EB' },
      { label: 'Technical Training',      icon: 'training',    color: '#2563EB' },
      { label: 'Assessments',             icon: 'assessment',  color: '#2563EB' },
      { label: 'Mini Project',            icon: 'project',     color: '#2563EB' },
      { label: 'Capstone Project',        icon: 'capstone',    color: '#2563EB' },
      { label: 'Live Project Internship', icon: 'team',        color: '#2563EB' },
      { label: 'Project Review',          icon: 'review',      color: '#2563EB' },
      { label: 'Certification',           icon: 'certificate', color: '#2563EB' },
      { label: 'Placement Support',       icon: 'placement',   color: '#2563EB' },
    ],
  },
  4: { // ESPRP
    title: 'ESPRP Journey Workflow',
    accentColor: '#4F46E5',
    steps: [
      { label: 'Enrollment',                 icon: 'enroll',        color: '#4F46E5' },
      { label: 'Skill Assessment',           icon: 'assessment',    color: '#4F46E5' },
      { label: 'Communication Training',     icon: 'communication', color: '#4F46E5' },
      { label: 'Aptitude & Coding Practice', icon: 'training',      color: '#4F46E5' },
      { label: 'Mock Interviews',            icon: 'interview',     color: '#4F46E5' },
      { label: 'Resume Building',            icon: 'resume',        color: '#4F46E5' },
      { label: 'HR & Technical Rounds',      icon: 'review',        color: '#4F46E5' },
      { label: 'Certification',              icon: 'certificate',   color: '#4F46E5' },
      { label: 'Placement Support',          icon: 'placement',     color: '#4F46E5' },
    ],
  },
};

/* ── What You Will Learn data ── */
const LEARN_CATEGORIES = [
  {
    title: 'Software Development Tracks',
    accent: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
    items: ['Java Full Stack Development', 'Full Stack C# .NET Development', 'MERN Stack Development', 'MEAN Stack Development', 'Python Programming', 'Python Full Stack Development', 'Advanced Python Full Stack Development'],
  },
  {
    title: 'Data & Analytics Tracks',
    accent: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/></svg>,
    items: ['Data Science', 'Data Engineering', 'Data Scientist', 'Data Analytics', 'Power BI', 'PySpark', 'Tableau'],
  },
  {
    title: 'Quality Engineering Tracks',
    accent: '#7C3AED', bg: '#FAF5FF', border: '#DDD6FE',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
    items: ['Automation Testing with Java & Selenium', 'Playwright Automation Testing'],
  },
  {
    title: 'Design & Marketing Tracks',
    accent: '#EA580C', bg: '#FFF7ED', border: '#FED7AA',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>,
    items: ['UI/UX Design', 'Digital Marketing', 'Business Analyst'],
  },
  {
    title: 'Cloud, DevOps & Security Tracks',
    accent: '#0EA5E9', bg: '#F0F9FF', border: '#BAE6FD',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>,
    items: ['Jenkins', 'DevOps', 'Cyber Security'],
  },
  {
    title: 'Artificial Intelligence Tracks',
    accent: '#4338CA', bg: '#EEF2FF', border: '#C7D2FE',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4338CA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>,
    items: ['Artificial Intelligence & Machine Learning', 'Generative AI & LLMs', 'Agentic AI & AI Agents', 'Generative AI Engineer'],
  },
]

/* ── Track mapping ── */
const TRACKS = [
  { name: 'Software Development',    accent:'#2563EB', bg:'#EFF6FF', border:'#BFDBFE' },
  { name: 'Data & Analytics',        accent:'#16A34A', bg:'#F0FDF4', border:'#BBF7D0' },
  { name: 'Quality Engineering',     accent:'#7C3AED', bg:'#FAF5FF', border:'#DDD6FE' },
  { name: 'Design & Marketing',      accent:'#EA580C', bg:'#FFF7ED', border:'#FED7AA' },
  { name: 'Cloud, DevOps & Security',accent:'#0EA5E9', bg:'#F0F9FF', border:'#BAE6FD' },
  { name: 'Artificial Intelligence', accent:'#4338CA', bg:'#EEF2FF', border:'#C7D2FE' },
  { name: 'Other',                   accent:'#64748B', bg:'#F8FAFC', border:'#E2E8F0' },
];

const getTrack = (name = '') => {
  const n = name.toUpperCase();
  if (n.includes('JAVA') || n.includes('FULL STACK') || n.includes('MERN') || n.includes('MEAN') || n.includes('PYTHON') || n.includes('REACT') || n.includes('ANGULAR') || n.includes('NODE') || n.includes('EXPRESS') || n.includes('FRONT END') || n.includes('WEB DEV')) return 'Software Development';
  if (n.includes('DATA') || n.includes('SCIENCE') || n.includes('POWER BI') || n.includes('PYSPARK') || n.includes('TABLEAU') || n.includes('MACHINE LEARNING') || n.includes('ANALYTICS') || n.includes('ANALYST') || n.includes('ENGINEER')) return 'Data & Analytics';
  if (n.includes('AUTOMATION') || n.includes('TESTING') || n.includes('PLAYWRIGHT') || n.includes('SELENIUM') || n.includes('QA')) return 'Quality Engineering';
  if (n.includes('UI') || n.includes('UX') || n.includes('DESIGN') || n.includes('MARKETING') || n.includes('BUSINESS') || n.includes('HR') || n.includes('HUMAN RESOURCE')) return 'Design & Marketing';
  if (n.includes('JENKINS') || n.includes('DEVOPS') || n.includes('CYBER') || n.includes('SECURITY') || n.includes('CLOUD') || n.includes('AWS') || n.includes('AZURE') || n.includes('DOCKER') || n.includes('KUBERNETES')) return 'Cloud, DevOps & Security';
  if (n.includes('GENERATIVE') || n.includes('AGENTIC') || n.includes('LLM') || n.includes('ARTIFICIAL') || n.includes('AI ') || n.includes(' AI')) return 'Artificial Intelligence';
  return 'Other';
};

/* ── Tech floating icons for hero ── */
const TECH_ICONS = [
  { label: 'Python', bg: '#3776AB', color: '#FFD43B', top: '8%',  left: '10%', size: 52 },
  { label: 'React',  bg: '#20232A', color: '#61DAFB', top: '5%',  left: '46%', size: 52 },
  { label: 'Java',   bg: '#E76F00', color: '#fff',    top: '52%', left: '78%', size: 52 },
  { label: 'JS',     bg: '#F7DF1E', color: '#000',    top: '72%', left: '28%', size: 48 },
  { label: '</>',    bg: '#1E3A5F', color: '#60A5FA', top: '42%', left: '40%', size: 52 },
]

/* ── Course image with colored fallback ── */
const CourseImage = ({ imgPath, name, accent, bg }) => {
  const [failed, setFailed] = React.useState(false);
  const initials = (name || '').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

  if (!imgPath || failed) {
    return (
      <div style={{ height:140, background:`linear-gradient(135deg, ${bg}, ${accent}22)`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, flexShrink:0 }}>
        <div style={{ width:56, height:56, borderRadius:14, background:accent, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 16px ${accent}55` }}>
          <span style={{ fontSize:20, fontWeight:900, color:'#fff' }}>{initials}</span>
        </div>
        <span style={{ fontSize:11, fontWeight:600, color:accent, opacity:0.7, maxWidth:160, textAlign:'center', lineHeight:1.3, padding:'0 8px' }}>{name}</span>
      </div>
    );
  }

  return (
    <img
      src={imgPath}
      alt={name}
      style={{ width:'100%', height:140, objectFit:'cover', display:'block', flexShrink:0 }}
      onError={() => setFailed(true)}
    />
  );
};

const ProgramDetail = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { id }    = useParams();
  const [showApply, setShowApply] = useState(null); // null | 'enroll' | 'download'

  // First course with a PDF for each program (from DB)
  const PROGRAM_DEFAULT_COURSE = {
    1: { courseId: 1,  courseName: 'JAVA FULLSTACK' },           // Coding Bootcamp
    2: { courseId: 15, courseName: 'JAVA WEB PROGRAMMING' },     // Developer Full Time
    3: { courseId: 19, courseName: 'Scrum master certification' },// Internship
    4: { courseId: 28, courseName: 'QUANTITATIVE-APTITUDE' },    // ESPRP
  };

  const handleApplySuccess = async () => {
    if (showApply === 'download') {
      setShowApply(null);
      const defaultCourse = PROGRAM_DEFAULT_COURSE[prog?.program_id] || {};
      let pdfFilePath = programImage?.brochure_url;
      if (!pdfFilePath && prog?.program_id) {
        try {
          const res = await axiosinstance.get(`/api/programImgAWS/${prog.program_id}`);
          pdfFilePath = res.data?.brochure_url;
        } catch {}
      }
      navigate('/download', {
        state: {
          programInterested:  prog?.program_name,
          Program_id:         prog?.program_id,
          interestedCourse:   defaultCourse.courseName,
          interestedCourseId: defaultCourse.courseId,
          pdfFilePath:        pdfFilePath,
          isProgramBrochure:  true,
        }
      });
    } else {
      setShowApply(null);
    }
  };

  const fetchProgramsById   = useSelector((s) => s.fectchProgramById.programById);
  const fetchProgramsdetail = useSelector((s) => s.getProgramsId.programsId);
  const programImage        = useSelector((s) => s.fetchProgramImageById.programImageById);

  useEffect(() => {
    dispatch(fetchProgramById(id));
    dispatch(fetchCourseList());
    dispatch(fetchProgramId(id));
    dispatch(fetchProgramImageById(id));
  }, [dispatch, id]);

  const prog    = fetchProgramsdetail?.[0];
  const courses = fetchProgramsById || [];

  // Coding Bootcamp: render its dedicated full page
  if (String(id) === '1') return <CodingBootcamp />;
  // Developer Full Time program: render its dedicated full page
  if (String(id) === '2') return <DeveloperFullTime />;
  // Internship program: render its dedicated full page
  if (String(id) === '3') return <InternshipPage />;
  // ESPRP program: render its dedicated full page
  if (String(id) === '4') return <ESPRPPage />;

  // Guard against stale Redux state from a previously visited program page
  const isStale = prog && String(prog.program_id) !== String(id);

  if (!prog || isStale) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#0B1629' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:48, height:48, border:'4px solid #2563EB', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }}/>
        <p style={{ color:'#94A3B8', fontSize:16 }}>Loading program...</p>
      </div>
    </div>
  );

  const programTitle = (prog.program_name || '').toUpperCase();
  const journey = JOURNEY_BY_PROGRAM[prog.program_id];

  return (
    <div style={{ fontFamily:"'Inter','Segoe UI',sans-serif", background:'#F8FAFC', minHeight:'100vh' }}>
      <style>{`
        #pd-hero        { background: linear-gradient(135deg,#050D1E 0%,#091525 40%,#0D1F3C 100%) !important; }
        #pd-learn       { background-color: #ffffff !important; }
        #pd-structure   { background-color: #F8FAFF !important; }
        #pd-assessment  { background-color: #ffffff !important; }
        #pd-roles       { background-color: #F8FAFF !important; }
        #pd-methodology { background-color: #ffffff !important; }
        #pd-body        { background-color: #F8FAFC !important; }
        #pd-faq         { background-color: #F8FAFF !important; }
        #pd-cta         { background: linear-gradient(135deg,#0B1A3E 0%,#1E3A8A 100%) !important; }
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 900px) {
          #pd-hero-grid       { grid-template-columns: 1fr !important; gap: 24px !important; }
          #pd-hero-right      { display: none !important; }
          #pd-learn-grid      { grid-template-columns: repeat(2,1fr) !important; }
          #pd-structure-grid  { grid-template-columns: 1fr !important; gap: 20px !important; }
          .pd-structure-arrow { display: none !important; }
          #pd-assessment-grid { grid-template-columns: 1fr !important; }
          #pd-roles-grid      { grid-template-columns: 1fr !important; }
          #pd-methodology-row { flex-direction: column !important; align-items: center !important; gap: 24px !important; }
          .pd-methodology-arrow { display: none !important; }
          #pd-faq-grid        { grid-template-columns: 1fr !important; }
          .pd-esprp-2col      { grid-template-columns: 1fr !important; gap: 24px !important; }
          #pd-esprp-duration  { grid-template-columns: 1fr !important; }
          #pd-esprp-duration2 { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .pd-2col-sm         { grid-template-columns: 1fr !important; }
          .pd-careerroles-grid{ grid-template-columns: 1fr !important; }
          .pd-faq-questions   { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          #pd-learn-grid      { grid-template-columns: 1fr !important; }
          .pd-hero-pills      { flex-wrap: wrap !important; }
        }
      `}</style>

      {/* ══ HERO ══ */}
      <section id="pd-hero" style={{ padding:'44px 5% 52px', position:'relative', overflow:'hidden', minHeight:480 }}>
        {/* ambient glow */}
        <div style={{ position:'absolute', top:'-15%', right:'10%', width:560, height:560, borderRadius:'50%', background:'radial-gradient(circle,rgba(37,99,235,0.22) 0%,transparent 65%)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:'-10%', left:'20%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle,rgba(14,165,233,0.1) 0%,transparent 65%)', pointerEvents:'none' }}/>

        {/* breadcrumb */}
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'rgba(255,255,255,0.4)', marginBottom:32, position:'relative', zIndex:2 }}>
          <Link to="/" style={{ color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>Home</Link>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          <Link to="/programs" style={{ color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>Programs</Link>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          <span style={{ color:'rgba(255,255,255,0.75)' }}>{prog.program_name}</span>
        </div>

        <div id="pd-hero-grid" style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'center', position:'relative', zIndex:2 }}>

          {/* LEFT */}
          <div>
            <h1 style={{ fontSize:42, fontWeight:900, color:'#fff', margin:'0 0 10px', lineHeight:1.1, letterSpacing:'-0.02em' }}>
              UPL {programTitle}
            </h1>
            <h2 style={{ fontSize:26, fontWeight:800, color:'#60A5FA', margin:'0 0 18px', lineHeight:1.3 }}>
              Transform Learning into<br/>Career Readiness
            </h2>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.6)', margin:'0 0 28px', lineHeight:1.75, maxWidth:480 }}>
              {prog.program_details || 'An intensive 3 to 6 months program to build in-demand IT skills with training, projects, assessments and placement readiness.'}
            </p>

            {/* Info pills */}
            <div className="pd-hero-pills" style={{ display:'flex', gap:12, flexWrap:'nowrap', marginBottom:32 }}>
              {[
                { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/></svg>, label:'DURATION', value: prog.duration || '3 – 6 Months' },
                { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, label:'TRAINING PERIOD', value:'2 Months' },
                { icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>, label:'MODE', value:'Online | Offline | Hybrid' },
              ].map((pill) => (
                <div key={pill.label} style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'10px 16px' }}>
                  <span style={{ color:'rgba(255,255,255,0.55)' }}>{pill.icon}</span>
                  <div>
                    <div style={{ fontSize:9, fontWeight:800, color:'rgba(255,255,255,0.4)', letterSpacing:'0.1em', textTransform:'uppercase' }}>{pill.label}</div>
                    <div style={{ fontSize:12.5, fontWeight:700, color:'#fff' }}>{pill.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <button onClick={() => setShowApply('enroll')} style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'13px 28px', background:'#2563EB', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, border:'none', cursor:'pointer', boxShadow:'0 4px 18px rgba(37,99,235,0.45)' }}>
                Enroll Now
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
              <button
                onClick={() => document.getElementById('courses-section')?.scrollIntoView({ behavior:'smooth' })}
                style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'13px 28px', background:'transparent', color:'#fff', border:'1.5px solid rgba(255,255,255,0.3)', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}
              >
                View Courses
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
            </div>
          </div>

          {/* RIGHT — Tech visual */}
          <div id="pd-hero-right" style={{ position:'relative', height:380, display:'flex', alignItems:'center', justifyContent:'center' }}>
            {/* central glow */}
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:320, height:320, borderRadius:'50%', background:'radial-gradient(circle,rgba(37,99,235,0.3) 0%,transparent 65%)', pointerEvents:'none' }}/>


            {/* Floating tech icon boxes */}
            {TECH_ICONS.map((t) => (
              <div key={t.label} style={{ position:'absolute', top:t.top, left:t.left, width:t.size, height:t.size, borderRadius:12, background:t.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:t.label.length > 2 ? 11 : 16, fontWeight:900, color:t.color, boxShadow:'0 4px 20px rgba(0,0,0,0.4)', border:'1px solid rgba(255,255,255,0.1)', zIndex:3 }}>
                {t.label}
              </div>
            ))}

            {/* Central developer icon */}
            <div style={{ width:200, height:200, borderRadius:'50%', background:'rgba(37,99,235,0.15)', border:'2px solid rgba(37,99,235,0.3)', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', zIndex:2 }}>
              <div style={{ width:140, height:140, borderRadius:'50%', background:'rgba(37,99,235,0.2)', border:'1.5px solid rgba(37,99,235,0.4)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                </svg>
              </div>
            </div>

            {/* orbit ring */}
            <div style={{ position:'absolute', width:280, height:280, borderRadius:'50%', border:'1px dashed rgba(96,165,250,0.2)', pointerEvents:'none' }}/>
          </div>
        </div>
      </section>

      {/* ══ WHAT YOU WILL LEARN ══ */}
      <section id="pd-learn" style={{ padding:'60px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <h2 style={{ textAlign:'center', fontSize:24, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.04em', margin:'0 0 40px' }}>WHAT YOU WILL LEARN</h2>
          <div id="pd-learn-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:22 }}>
            {LEARN_CATEGORIES.map((cat) => (
              <div key={cat.title} style={{ border:`1.5px solid ${cat.border}`, borderRadius:14, padding:'24px', background:cat.bg }}>
                {/* header */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
                  <div style={{ width:48, height:48, borderRadius:12, background:'#fff', border:`1px solid ${cat.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {cat.icon}
                  </div>
                  <h3 style={{ fontSize:14.5, fontWeight:800, color:cat.accent, margin:0, lineHeight:1.3 }}>{cat.title}</h3>
                </div>
                {/* items */}
                <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:8 }}>
                  {cat.items.map((item) => (
                    <li key={item} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#374151' }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:cat.accent, flexShrink:0 }}/>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROGRAM STRUCTURE ══ */}
      <section id="pd-structure" style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <h2 style={{ textAlign:'center', fontSize:22, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.06em', margin:'0 0 48px' }}>PROGRAM STRUCTURE</h2>
          <div id="pd-structure-grid" style={{ display:'grid', gridTemplateColumns:'1fr 32px 1fr 32px 1fr 32px 1fr', alignItems:'start' }}>
            {[
              { num:1, phase:'PHASE 1', title:'Foundation Training\n(Month 1)', bg:'#EFF6FF', border:'#BFDBFE', color:'#1E3A8A', btnColor:'#2563EB', items:['Programming Fundamentals','Logic Building','Problem Solving','Database Concepts','Git & Version Control','API Integration','Industry Tools'], activity:'Daily Coding Practice, Assignments, Weekly Evaluations, Mini Tasks' },
              { num:2, phase:'PHASE 2', title:'Advanced Training\n(Month 2)', bg:'#F0FDF4', border:'#BBF7D0', color:'#14532D', btnColor:'#16A34A', items:['Frameworks','APIs','Deployment','SDLC','Testing','Documentation'], activity:'Case Studies, Live Coding Sessions, Team Activities, Hackathons' },
              { num:3, phase:'PHASE 3', title:'Capstone Project', bg:'#FAF5FF', border:'#DDD6FE', color:'#4C1D95', btnColor:'#7C3AED', items:['Real-world Projects','End-to-End Development','Team Collaboration','Client Requirements','Code Review','Deployment'], activity:'Requirement Doc, Design, Development, Testing, Deployment, Presentation' },
              { num:4, phase:'PHASE 4', title:'Assessment &\nPlacement Readiness', bg:'#FFF7ED', border:'#FED7AA', color:'#EA580C', btnColor:'#EA580C', items:['Written Test Training','Mock Interviews','Resume Preparation','Career Guidance','Placement Support'], activity:'' },
            ].map((ph, i, arr) => (
              <React.Fragment key={ph.num}>
                <div style={{ background:ph.bg, border:`1.5px solid ${ph.border}`, borderRadius:14, padding:'24px 18px' }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', background:ph.btnColor, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:18, color:'#fff', margin:'0 auto 12px' }}>{ph.num}</div>
                  <div style={{ textAlign:'center', fontWeight:900, fontSize:13, color:ph.color, marginBottom:2 }}>{ph.phase}</div>
                  <div style={{ textAlign:'center', fontWeight:700, fontSize:13, color:ph.color, marginBottom:14, lineHeight:1.4, whiteSpace:'pre-line' }}>{ph.title}</div>
                  {ph.items.map(item => (
                    <div key={item} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12.5, color:'#374151', marginBottom:7 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={ph.btnColor} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {item}
                    </div>
                  ))}
                  {ph.activity && <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${ph.border}` }}><div style={{ fontSize:12, fontWeight:700, color:ph.btnColor, marginBottom:4 }}>Activities</div><div style={{ fontSize:12, color:'#6B7280', lineHeight:1.6 }}>{ph.activity}</div></div>}
                </div>
                {i < arr.length - 1 && (
                  <div className="pd-structure-arrow" style={{ display:'flex', alignItems:'center', justifyContent:'center', paddingTop:96 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ══ JOURNEY WORKFLOW ══ */}
      {journey && (
        <JourneyWorkflow title={journey.title} steps={journey.steps} accentColor={journey.accentColor} />
      )}

      {/* ══ ASSESSMENT & MOCK INTERVIEW ══ */}
      <section id="pd-assessment" style={{ padding:'64px 5%' }}>
        <div id="pd-assessment-grid" style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:32 }}>
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'32px 28px' }}>
            <h3 style={{ fontWeight:900, fontSize:16, color:'#1E3A8A', textAlign:'center', margin:'0 0 6px', letterSpacing:'0.03em' }}>WRITTEN TEST ASSESSMENT TRAINING</h3>
            <p style={{ textAlign:'center', fontSize:13, color:'#6B7280', margin:'0 0 24px' }}>We prepare you to crack placement assessments with confidence.</p>
            <div className="pd-2col-sm" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {[
                { title:'Aptitude', color:'#2563EB', items:['Quantitative Aptitude','Logical Reasoning','Analytical Thinking'] },
                { title:'Coding Assessment', color:'#7C3AED', items:['Problem Solving','Algorithms','Coding Challenges'] },
                { title:'Soft Skills', color:'#0EA5E9', items:['Communication','Presentation Skills','Workplace Etiquette'] },
                { title:'Puzzles & Problem Analysis', color:'#EA580C', items:['Case Analysis','Scenario Solving','Critical Thinking'] },
              ].map(c => (
                <div key={c.title} style={{ border:'1px solid #E0E7FF', borderRadius:10, padding:'16px' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:c.color, marginBottom:10 }}>{c.title}</div>
                  {c.items.map(it => (
                    <div key={it} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#374151', marginBottom:5 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:c.color, flexShrink:0, display:'inline-block' }}/>{it}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'32px 28px' }}>
            <h3 style={{ fontWeight:900, fontSize:16, color:'#1E3A8A', textAlign:'center', margin:'0 0 6px', letterSpacing:'0.03em' }}>MOCK INTERVIEW PREPARATION</h3>
            <p style={{ textAlign:'center', fontSize:13, color:'#6B7280', margin:'0 0 24px' }}>Be interview-ready with expert guidance and practice.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {[
                { title:'Technical Interviews', color:'#2563EB', tags:['Programming Questions','Projects Discussion','Coding Tests'] },
                { title:'HR Interviews', color:'#7C3AED', tags:['Self Introduction','Behavioral Questions','Communication Training'] },
                { title:'Group Discussions', color:'#EA580C', tags:['Leadership','Teamwork','Presentation'] },
              ].map(row => (
                <div key={row.title} style={{ display:'flex', alignItems:'center', gap:16, border:'1px solid #E0E7FF', borderRadius:10, padding:'16px 18px' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:row.color, marginBottom:6 }}>{row.title}</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                      {row.tags.map((tag,i) => <span key={tag} style={{ fontSize:12, color:'#6B7280' }}>{i > 0 && <span style={{ marginRight:8, color:'#D1D5DB' }}>•</span>}{tag}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ KEY FEATURES + CAREER ROLES + DELIVERABLES ══ */}
      <section id="pd-roles" style={{ padding:'64px 5%' }}>
        <div id="pd-roles-grid" style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1.6fr 1fr', gap:28 }}>
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'28px 24px' }}>
            <h3 style={{ fontWeight:900, fontSize:15, color:'#1E3A8A', textAlign:'center', margin:'0 0 20px', letterSpacing:'0.04em' }}>KEY FEATURES</h3>
            {['Industry-Focused Curriculum','Hands-on Practical Sessions','Real-world Projects & Assignments','Capstone Project','Written Test Preparation','Mock Interviews','Resume Building Support','Career Guidance','Industry Mentors'].map(f => (
              <div key={f} style={{ display:'flex', alignItems:'center', gap:9, fontSize:13, color:'#374151', marginBottom:10 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{f}
              </div>
            ))}
          </div>
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'28px 24px' }}>
            <h3 style={{ fontWeight:900, fontSize:15, color:'#1E3A8A', textAlign:'center', margin:'0 0 20px', letterSpacing:'0.04em' }}>CAREER ROLES YOU CAN TARGET</h3>
            <div className="pd-careerroles-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[{icon:'💻',label:'Software Developer'},{icon:'📊',label:'Data Analyst'},{icon:'🖥️',label:'Frontend Developer'},{icon:'☁️',label:'Cloud Associate'},{icon:'⚙️',label:'Backend Developer'},{icon:'🎨',label:'UI Developer'},{icon:'🔗',label:'Full Stack Developer'},{icon:'🧪',label:'Testing Engineer'},{icon:'🤖',label:'AI Engineer'},{icon:'👨‍💻',label:'Junior Developer'},{icon:'📈',label:'ML Engineer'},{icon:'➕',label:'and more...'}].map(r => (
                <div key={r.label} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', border:'1px solid #EEF2FF', borderRadius:8, fontSize:12.5, color:'#374151', fontWeight:500 }}>
                  <span style={{ fontSize:16 }}>{r.icon}</span>{r.label}
                </div>
              ))}
            </div>
          </div>
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'28px 24px' }}>
            <h3 style={{ fontWeight:900, fontSize:15, color:'#1E3A8A', textAlign:'center', margin:'0 0 20px', letterSpacing:'0.04em' }}>DELIVERABLES</h3>
            {[{icon:'🏆',label:'Training Completion Certificate'},{icon:'📋',label:'Capstone Project Certificate'},{icon:'🏅',label:'Internship Recommendation (Eligible)'},{icon:'👤',label:'Portfolio Support'},{icon:'📄',label:'Resume Assistance'},{icon:'🐙',label:'GitHub Portfolio Guidance'}].map(d => (
              <div key={d.label} style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:13, color:'#374151', marginBottom:14 }}>
                <span style={{ fontSize:18, flexShrink:0 }}>{d.icon}</span>
                <span style={{ lineHeight:1.5 }}>{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ LEARNING METHODOLOGY ══
      <section id="pd-methodology" style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', border:'1.5px solid #E0E7FF', borderRadius:18, padding:'44px 48px', background:'#fff' }}>
          <h2 style={{ textAlign:'center', fontSize:22, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.06em', margin:'0 0 48px' }}>LEARNING METHODOLOGY</h2>
          <div id="pd-methodology-row" style={{ display:'flex', alignItems:'flex-start', justifyContent:'center' }}>
            {[
              { label:'TRAIN',     desc:'Learn concepts from experts' },
              { label:'BUILD',     desc:'Build projects and applications' },
              { label:'ASSESS',    desc:'Evaluate through tests and assignments' },
              { label:'INTERVIEW', desc:'Mock interviews and feedback' },
              { label:'DEPLOY',    desc:'Get placed & grow your career' },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div style={{ textAlign:'center', width:160, flexShrink:0 }}>
                  <div style={{ width:80, height:80, borderRadius:'50%', background:'#EFF6FF', border:'1.5px solid #BFDBFE', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:28 }}>
                    {['📚','💻','📝','🎤','🚀'][i]}
                  </div>
                  <div style={{ fontSize:14, fontWeight:900, color:'#1E3A8A', marginBottom:8, letterSpacing:'0.04em' }}>{step.label}</div>
                  <div style={{ fontSize:12.5, color:'#6B7280', lineHeight:1.6 }}>{step.desc}</div>
                </div>
                {i < arr.length - 1 && (
                  <div className="pd-methodology-arrow" style={{ display:'flex', alignItems:'center', paddingBottom:52, flexShrink:0, width:48, justifyContent:'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
      ══ */}

      {/* ══ PROGRAM CONTENT ══ */}
      <section id="pd-body" style={{ padding:'0 5% 80px' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>

          {/* ── Courses by Track ── */}
          {courses.length > 0 && (() => {
            // Group courses by track
            const grouped = {};
            courses.forEach(course => {
              const track = getTrack(course.course_name);
              if (!grouped[track]) grouped[track] = [];
              grouped[track].push(course);
            });
            // Render in defined track order
            return (
              <div>
                <h2 style={{ fontSize:26, fontWeight:900, color:'#0F172A', margin:'0 0 6px', letterSpacing:'-0.02em' }}>Included Courses</h2>
                <p style={{ ...bodyText, marginBottom:36 }}>Browse courses by track — each track covers a focused skill domain</p>
                {TRACKS.filter(t => grouped[t.name]?.length > 0).map(track => (
                  <div key={track.name} style={{ marginBottom:48 }}>
                    {/* Track header */}
                    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, paddingBottom:12, borderBottom:`2px solid ${track.border}` }}>
                      <div style={{ width:10, height:10, borderRadius:'50%', background:track.accent, flexShrink:0 }}/>
                      <h3 style={{ fontSize:16, fontWeight:800, color:track.accent, margin:0, letterSpacing:'0.04em', textTransform:'uppercase' }}>{track.name}</h3>
                      <span style={{ fontSize:12, fontWeight:600, color:track.accent, background:track.bg, border:`1px solid ${track.border}`, borderRadius:50, padding:'2px 10px' }}>
                        {grouped[track.name].length} {grouped[track.name].length === 1 ? 'Course' : 'Courses'}
                      </span>
                    </div>
                    {/* Course cards */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:18 }}>
                      {grouped[track.name].map((course, i) => (
                        <Link key={i} to={`/courses/${course.course_id}`} state={{ interestedProgram:prog.program_name, Program_id:prog.program_id }} style={{ textDecoration:'none' }}>
                          <div style={{ background:'#fff', border:`1.5px solid ${track.border}`, borderRadius:12, overflow:'hidden', cursor:'pointer', transition:'transform 0.2s, box-shadow 0.2s', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', display:'flex', flexDirection:'column', height:'100%' }}
                            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.12)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.05)'; }}
                          >
                            <div style={{ height:4, background:track.accent, flexShrink:0 }}/>
                            {/* Image or colored placeholder */}
                            <CourseImage imgPath={course.img_Path} name={course.course_name} accent={track.accent} bg={track.bg} />
                            <div style={{ padding:'14px 16px 16px', flex:1, display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
                              <h4 style={{ fontSize:13.5, fontWeight:700, color:'#0F172A', margin:'0 0 10px', lineHeight:1.4 }}>{course.course_name}</h4>
                              <span style={{ fontSize:11.5, fontWeight:600, color:track.accent, background:track.bg, padding:'3px 10px', borderRadius:50, border:`1px solid ${track.border}`, alignSelf:'flex-start' }}>View Details →</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Right — sidebar (commented out) */}
          {/* <div style={{ position:'sticky', top:80 }}>
            <div style={card}>
              <h2 style={{ fontSize:18, fontWeight:800, color:'#0F172A', margin:'0 0 14px' }}>Program Highlights</h2>
              ...
            </div>
          </div> */}

        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section id="pd-faq" style={{ padding:'64px 5%' }}>
        <div id="pd-faq-grid" style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 320px', gap:28, alignItems:'start' }}>
          <div>
            <h2 style={{ fontSize:20, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.04em', margin:'0 0 24px' }}>FREQUENTLY ASKED QUESTIONS</h2>
            <div className="pd-faq-questions" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[
                'Who can join this program?','Is project work included?',
                'What is the duration?','Is placement preparation included?',
                'What is the training mode?','Will I get a certificate?',
              ].map(q => (
                <div key={q} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', border:'1.5px solid #E0E7FF', borderRadius:10, padding:'16px 18px', background:'#fff', gap:12 }}>
                  <span style={{ fontSize:13.5, color:'#374151', fontWeight:500, lineHeight:1.4 }}>{q}</span>
                  <span style={{ width:24, height:24, borderRadius:'50%', border:'1.5px solid #BFDBFE', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'#2563EB', fontSize:18, fontWeight:300 }}>+</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'32px 28px', background:'#fff', textAlign:'center' }}>
            <div style={{ width:72, height:72, borderRadius:'50%', background:'#EFF6FF', border:'1.5px solid #BFDBFE', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </div>
            <h3 style={{ fontSize:16, fontWeight:900, color:'#1E3A8A', margin:'0 0 12px' }}>COLLEGES / INSTITUTIONS</h3>
            <p style={{ fontSize:13, color:'#6B7280', lineHeight:1.7, margin:'0 0 24px' }}>We invite colleges to partner with us for training, assessments, and placement readiness programs.</p>
            <Link to="/contact" style={{ display:'inline-block', padding:'12px 32px', background:'#2563EB', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, textDecoration:'none', boxShadow:'0 4px 14px rgba(37,99,235,0.35)' }}>Send Enquiry</Link>
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section id="pd-cta" style={{ padding:'56px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between', gap:40, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:280 }}>
            <h2 style={{ fontSize:'clamp(22px,3vw,34px)', fontWeight:800, color:'#FFFFFF', margin:'0 0 12px', letterSpacing:'-0.02em' }}>
              Ready to Start Your Journey?
            </h2>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.7)', lineHeight:1.7, margin:0 }}>
              Join thousands of students who have transformed their careers with {prog.program_name}.
            </p>
          </div>
          <div style={{ display:'flex', gap:14, flexWrap:'wrap', flexShrink:0 }}>
            <button onClick={() => setShowApply('enroll')} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'14px 32px', background:'#FFFFFF', color:'#2563EB', borderRadius:10, fontSize:15, fontWeight:700, border:'none', cursor:'pointer', boxShadow:'0 4px 16px rgba(0,0,0,0.15)' }}>
              Enroll Now →
            </button>
            <Link to="/contact" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'13px 28px', background:'transparent', color:'#FFFFFF', border:'2px solid rgba(255,255,255,0.4)', borderRadius:10, fontSize:15, fontWeight:700, textDecoration:'none' }}>
              Talk to an Advisor
            </Link>
          </div>
        </div>
      </section>

      {showApply && (
        <RequestCallback isModal onClose={() => setShowApply(null)} onSuccess={handleApplySuccess} defaultProgram={prog?.program_name || ''} />
      )}
    </div>
  );
};

/* ── Shared styles ── */
const card        = { background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:14, padding:'26px 30px', boxShadow:'0 2px 12px rgba(37,99,235,0.06)' };
const sectionLabel= { fontSize:14.5, fontWeight:700, color:'#0F172A', margin:'0 0 6px' };
const bodyText    = { fontSize:14, lineHeight:1.75, color:'#475569', margin:0 };
const iconBadge   = { width:36, height:36, borderRadius:9, background:'#EFF6FF', border:'1px solid #BFDBFE', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 };

export default ProgramDetail;
